import { TitanBitmapTextField } from "@lib/titan2/dist/game/control/TitanBitmapTextField";
import { TitanButton } from "@lib/titan2/dist/game/control/TitanButton";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { BitmapTextFieldProperties } from "@lib/titan2/dist/game/control/properties/BitmapTextFieldProperties";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import gsap from "gsap";
import { GameState } from "../../../../api/model/enum/GameState";
import { SpineAnimation } from "../style/SpineAnimation";
import { AbstractGameView } from "./AbstractGameView";
/**
 * TicketSymbol.ts
 */
export class TicketSymbol extends AbstractGameView {
    constructor() {
        super(...arguments);
        this.HIGHLIGHT_PULSE_SCALE = 1.1;
        this.HIGHLIGHT_PULSE_DURATION = 0.9;
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    destroy() {
        gsap.killTweensOf(this);
        super.destroy();
        DestroyUtil.destroy(this.button);
        DestroyUtil.destroy(this.symbol);
        DestroyUtil.destroy(this.spine);
        DestroyUtil.destroy(this.container);
        DestroyUtil.destroy(this.overlaySpine);
        DestroyUtil.destroy(this.symbolHighlight);
        DestroyUtil.destroy(this.symbolHighlightSuperWild);
        DestroyUtil.destroy(this.background);
        this.button = null;
        this.symbol = null;
        this.data = null;
        this.container = null;
        this.enabled = false;
        this.spine = null;
        this.dabbing = false;
        this.landscapeProperties = null;
        this.portraitProperties = null;
        this.uiProperties = null;
        this.overlaySpine = null;
        this.symbolHighlight = null;
        this.symbolHighlightSuperWild = null;
        this.background = null;
    }
    create() {
        this.symbolScale = 1;
        super.create();
        this.button = TitanButton.createButton(this, this.landscapeProperties.buttonProperties, this.portraitProperties?.buttonProperties, this.handleButtonSelected.bind(this));
        this.button.name = "dabBtn";
        this.button.renderable = !this.uiProperties.hideButton;
        this.container = ViewUtils.createContainer(this, "TicketSymbol");
        this.createStates();
        this.createSymbol();
        this.enabled = true;
    }
    onSelection(handler, scope) {
        this.selectionHandler = new EventHandler(handler, scope);
    }
    createSymbol() {
        if (this.uiProperties.symbolFieldProperties instanceof BitmapTextFieldProperties) {
            this.symbol = new TitanBitmapTextField(this.landscapeProperties.symbolFieldProperties, this.portraitProperties?.symbolFieldProperties);
            this.addChild(this.symbol);
            if (this.landscapeProperties.symbolHighlightFieldProperties) {
                this.symbolHighlight = new TitanBitmapTextField(this.landscapeProperties.symbolHighlightFieldProperties, this.portraitProperties?.symbolHighlightFieldProperties);
            }
            if (this.landscapeProperties.symbolHighlightSuperWildFieldProperties) {
                this.symbolHighlightSuperWild = new TitanBitmapTextField(this.landscapeProperties.symbolHighlightSuperWildFieldProperties, this.portraitProperties?.symbolHighlightSuperWildFieldProperties);
            }
        }
        else {
            this.symbol = ViewUtils.createField(this, this.landscapeProperties.symbolFieldProperties, this.portraitProperties?.symbolFieldProperties);
            if (this.landscapeProperties.symbolHighlightFieldProperties) {
                this.symbolHighlight = ViewUtils.createField(this, this.landscapeProperties.symbolHighlightFieldProperties, this.portraitProperties?.symbolHighlightFieldProperties);
            }
            if (this.landscapeProperties.symbolHighlightSuperWildFieldProperties) {
                this.symbolHighlightSuperWild = ViewUtils.createField(this, this.landscapeProperties.symbolHighlightSuperWildFieldProperties, this.portraitProperties?.symbolHighlightSuperWildFieldProperties);
            }
        }
    }
    addEventListeners() {
        //super.addEventListeners();
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
    }
    removeEventListeners() {
        //super.removeAllListeners();
        this.unregisterGame(CommonGameEvent.SPIN_REQUEST);
    }
    handleButtonSelected() {
        console.log("button pressed");
        this.callHandler(this.selectionHandler, this);
    }
    update(data) {
        this.data = data;
        this.reset();
        this.updateSymbol();
    }
    updateSymbol() {
        this.symbol.setText(this.data.getNumber().toString());
        this.addSymbol(this.symbol);
        if (this.symbolHighlight) {
            this.symbolHighlight.setText(this.data.getNumber().toString());
            this.addSymbol(this.symbolHighlight);
        }
        if (this.symbolHighlightSuperWild) {
            this.symbolHighlightSuperWild.setText(this.data.getNumber().toString());
            this.addSymbol(this.symbolHighlightSuperWild);
        }
    }
    reset() {
        this.removeSymbols();
        this.hideWin();
        this.dabbing = false;
    }
    disable() {
        this.removeSymbols();
        if (this.data && !this.data.isDabbed()) {
            this.addSymbol(this.symbol);
        }
        this.button.setEnabled(false);
        this.enabled = false;
        if (this.dabbing) {
            this.addSymbol(this.symbolHighlight);
        }
        else {
            this.removeHighlight();
        }
    }
    enable() {
        console.log("enabling button!");
        this.button.setEnabled(true);
        this.button.accessibleHint = this.data.getNumber().toString();
        this.enabled = true;
    }
    removeSymbols() {
        this.removeSymbol(this.symbol);
        this.removeSymbol(this.symbolHighlight);
        this.removeSymbol(this.symbolHighlightSuperWild);
    }
    showSymbolHighlight() {
        this.removeSymbol(this.symbol);
        this.removeSymbol(this.symbolHighlightSuperWild);
        this.addSymbol(this.symbolHighlight);
    }
    highlight(animate) {
        Logger.verbose("highlight");
        SpineUtil.play(this.spine, SpineAnimation.HIGHLIGHT);
        this.removeSymbols();
        this.addSymbol(this.symbolHighlight);
        if (animate) {
            this.pulseStart();
        }
    }
    highlightSuperWild(animate) {
        Logger.verbose("highlightSuperWild");
        SpineUtil.play(this.spine, SpineAnimation.HIGHLIGHT_SUPERWILD);
        this.removeSymbols();
        this.addSymbol(this.symbolHighlightSuperWild);
        if (animate) {
            this.pulseStart();
        }
    }
    showWin() {
        if (!this.data.isDabbed()) {
            SpineUtil.gotoAndStop(this.spine, 0, SpineAnimation.AVAILABLE_WIN);
        }
    }
    hideWin() {
        if (!this.data.isDabbed()) {
            this.removeSymbols();
            this.addSymbol(this.symbol);
            this.removeHighlight();
        }
    }
    dabSuccess() {
        this.container.removeChild(this.button);
        this.bringToTop();
        this.model.setTicketDabInProgress(this);
        Logger.verbose("dabSuccess");
        if (this.model.isExpressGame()) {
            if (this.model.getState() !== GameState.STANDARD_SPIN && this.model.getState() !== GameState.FREE_SPIN) {
                SpineUtil.play(this.overlaySpine, SpineAnimation.DAB, () => { this.model.setTicketDabComplete(this); });
            }
        }
        else {
            SpineUtil.play(this.overlaySpine, SpineAnimation.DAB, () => { this.model.setTicketDabComplete(this); });
        }
        this.dabbing = true;
    }
    flash() {
        SpineUtil.play(this.spine, SpineAnimation.FLASH, this.handleFlashComplete, this);
    }
    fullHouse() {
        this.overlaySpine.visible = true;
        Logger.verbose("fullhouse");
        SpineUtil.play(this.overlaySpine, SpineAnimation.FULL_HOUSE, this.handleFullHouseComplete, this);
    }
    bigWin() {
        this.overlaySpine.visible = true;
        Logger.verbose("bigWin");
        SpineUtil.play(this.overlaySpine, SpineAnimation.BIG_WIN, this.handleFullHouseComplete, this);
    }
    handleFlashComplete() {
        this.removeHighlight();
    }
    handleFullHouseComplete() {
        this.reset();
    }
    dab() {
        this.container.removeChild(this.button);
        this.highlight(false);
        this.spine.visible = true;
        this.model.setTicketDabInProgress(this);
        Logger.verbose("dab");
        if (this.model.isExpressGame()) {
            if (this.model.getState() !== GameState.STANDARD_SPIN && this.model.getState() !== GameState.FREE_SPIN) {
                SpineUtil.play(this.overlaySpine, SpineAnimation.DAB, () => { this.model.setTicketDabComplete(this); });
            }
        }
        else {
            SpineUtil.play(this.overlaySpine, SpineAnimation.DAB, () => { this.model.setTicketDabComplete(this); });
        }
    }
    dabAfterHighlight() {
        Logger.verbose("dab");
        SpineUtil.play(this.overlaySpine, SpineAnimation.DAB);
    }
    win() {
        if (this.data.isDabbed()) {
            Logger.verbose("win");
            if (this.model.isExpressGame()) {
                if (this.model.getState() !== GameState.STANDARD_SPIN && this.model.getState() !== GameState.FREE_SPIN) {
                    SpineUtil.play(this.overlaySpine, SpineAnimation.DAB);
                }
            }
            else {
                SpineUtil.play(this.overlaySpine, SpineAnimation.DAB);
            }
            this.spine.visible = true;
            this.overlaySpine.visible = true;
        }
        else {
            this.dabSuccess();
        }
    }
    createStates() {
        this.spine = TitanSpine.createSpine(this.container, this.landscapeProperties.spineProperties, this.portraitProperties?.spineProperties);
        this.overlaySpine = TitanSpine.createSpine(this.container, this.landscapeProperties.spineProperties, this.portraitProperties?.spineProperties);
        this.overlaySpine.visible = false;
    }
    removeHighlight() {
        Logger.verbose("removeHighlight");
        let state = this.data && this.data.isDabbed() ? SpineAnimation.DABBED : SpineAnimation.DEFAULT;
        SpineUtil.gotoAndStop(this.spine, 0, state);
        this.removeSymbols();
        this.addSymbol(this.symbol);
        this.pulseEnd();
    }
    bringToTop() {
        if (this.parent) {
            this.parent.addChildAt(this, this.parent.children.length - 1);
        }
    }
    getData() {
        return this.data;
    }
    // TitanPanel arrange functions use the symbol container'swidth and height, which is not consistent depending on what is rendererd. Use the button dimensions instead.
    getWidth() {
        return this.button.width;
    }
    getHeight() {
        return this.button.height;
    }
    addSymbol(symbol) {
        if (symbol) {
            this.container.addChildAt(symbol, this.container.getChildIndex(this.overlaySpine));
        }
    }
    isEnabled() {
        return this.enabled;
    }
    removeSymbol(symbol) {
        if (symbol) {
            this.container.removeChild(symbol);
        }
    }
    getPanelWidth() {
        return this.uiProperties.size.x;
    }
    getPanelHeight() {
        return this.uiProperties.size.y;
    }
    getSpine() {
        return this.spine;
    }
    pulseStart() {
        Logger.verbose("pulseStart");
        this.symbolScale = 1;
        this.symbolTween = gsap.to(this, {
            symbolScale: this.HIGHLIGHT_PULSE_SCALE,
            yoyo: true,
            repeat: -1,
            duration: this.HIGHLIGHT_PULSE_DURATION,
            onUpdate: () => {
                this.symbol.getHolder().scale.set(this.symbolScale);
                this.symbolHighlight.getHolder().scale.set(this.symbolScale);
                this.symbolHighlightSuperWild.getHolder().scale.set(this.symbolScale);
            }
        });
    }
    pulseEnd() {
        Logger.verbose("pulseEnd");
        const progress = this.symbolTween?.progress() || 1;
        const duration = this.HIGHLIGHT_PULSE_DURATION * progress;
        this.symbolTween?.kill();
        if (this.symbolScale !== 1) {
            this.symbolTween = gsap.to(this, {
                symbolScale: 1,
                duration: duration,
                onUpdate: () => {
                    this.symbol.getHolder().scale.set(this.symbolScale);
                    this.symbolHighlight.getHolder().scale.set(this.symbolScale);
                    this.symbolHighlightSuperWild.getHolder().scale.set(this.symbolScale);
                }
            });
        }
        else {
            this.symbolTween = null;
        }
    }
    handleSpinRequest() {
        if (this) {
            gsap.killTweensOf(this);
            this.pulseEnd();
        }
    }
}
//# sourceMappingURL=TicketSymbol.js.map